/**
 * Login Helper - Handles localStorage setup after successful login
 * Used by LoginExisting.vue, LoginBoard.vue, LoginFamily.vue, and SsoVerify.vue
 */

import axios from "axios";
import Package from "/package.json";
import emptyProfilePic from "@/assets/Menu/Empty_Profile_Pic.jpg";
import { public_api } from "@/services/api";

/**
 * Invalidate the current JWT server-side (via Guardian) before clearing it
 * from localStorage. Without this, logging out only clears the browser's
 * copy of the token — the token itself stays valid (stateless JWT) until
 * its natural expiry, so a captured/leaked token would still work after
 * the user "logged out". Call this BEFORE removing TOKEN from localStorage
 * (the Authorization header is read from localStorage at request time).
 *
 * Best-effort: never throws, never blocks logout on network failure.
 */
export async function invalidateTokenOnLogout() {
  try {
    await public_api.post("sessions/logout");
  } catch (e) {
    // Logout must proceed regardless — the token will still expire naturally.
    console.warn("[logout] Could not invalidate token server-side:", e?.message);
  }
}

/**
 * Process login response and set up localStorage
 * @param {Object} data - Login response data
 * @param {string} domain - domain prefix
 * @param {Object} router - Vue router instance
 * @param {Object} vueInstance - Vue component instance (for screen_auth, global methods)
 * @param {string} applicationVersion - app version
 */
export async function processLoginResponse(data, domain, router, vueInstance, applicationVersion = null, product_type = 'STARR') {
  localStorage.setItem("resultat", applicationVersion);

  if (data.result !== "success") {
    throw new Error("Invalid credentials");
  }

  localStorage.setItem("TOKEN", data.token);

  if (domain.includes(["admin","devadmin"]) == false) {
    await processCompanyLogin(data, domain, router, vueInstance, product_type);
  } else {
    await processAdminLogin(data, domain, router, vueInstance);
  }
}

/**
 * Process company login (non-admin)
 */
async function processCompanyLogin(data, domain, router, vueInstance, product_type = 'STARR') {
  try {
    // If the login response carried the user's full cross-company entity list
    // (e.g. client_magic_login after signup), store it the same way
    // handle_domain_login_success does so the "Switch Company" button in
    // TopNavBar (has_multi_domain) shows up immediately — otherwise it only
    // appears after the user separately logs in again via the domain-login page.
    if (Array.isArray(data.entities) && data.entities.length) {
      localStorage.setItem("ALL_ENTITIES", JSON.stringify(data.entities));
    }

    const response = await vueInstance.$api.get("get_features_by_role/" + data.user_id);

    // A 402 here means the entity being switched into is ALSO unpaid —
    // api.js's response interceptor already set PAYMENT_BLOCKED and kicked
    // off a redirect to /payment-required for it, but swallows the error
    // (resolves with `{ data: {} }`) instead of rejecting so callers don't
    // have to special-case it everywhere. Bail out here rather than crash
    // on `.data[0]` of an empty response while that redirect is in flight.
    if (!response.data?.data) {
      return;
    }

    if (response.data.data[0].entity_list.length === 0) {
      alert("Client is Deactivated");
      localStorage.clear();
      throw new Error("Client is Deactivated");
    }

    const entity_list_sorted = [...response.data.data[0].entity_list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    localStorage.setItem("entitydata", JSON.stringify(entity_list_sorted));
    localStorage.setItem("IS_ADMIN", response.data.is_admin.toString());
    localStorage.setItem("IS_CEO", (response.data.is_ceo || false).toString());
    localStorage.setItem("IS_HR", (response.data.is_hr || false).toString());
    localStorage.setItem("CLIENT_COUNTRY", response.data.country || "");
    localStorage.setItem("CLIENT_CURRENCY", response.data.currency || "");
    localStorage.setItem("CLIENT_INDUSTRY_NAMES", JSON.stringify(response.data.industry_names || []));
    localStorage.setItem("family_office_flag", response.data.data[0].family_office_flag);
    localStorage.setItem("family_tree_flag", response.data.data[0].family_tree_flag);

    // Get domain logo
    getDomainLogo(domain, vueInstance);

    let enty = entity_list_sorted;
    if (enty.length === 1) {
      localStorage.setItem("ENTITY_ID", enty[0].id);
      localStorage.setItem("entity_name", enty[0].name);
      localStorage.setItem("ENT_FINYEAR", enty[0].financial_year != null ? enty[0].financial_year : "__NA__");
    }

    // Same lookup FBP_Statements.vue's Industry Benchmark column makes
    // (setup_guide/industry_info -> current_user.entity_id -> entity -> client ->
    // industry). Saved here too so it's visible in localStorage right after login,
    // alongside ENTITY_ID / CLIENT_*.
    //
    // Deliberately OUTSIDE the `enty.length === 1` block above: this endpoint reads
    // conn.assigns.current_user.entity_id from the authenticated user's own JWT, not
    // from entity_list_sorted (the list of entities available to switch into) — so it
    // doesn't need — and must not wait on — a single-entity match. Gating it there
    // meant it silently never ran for any multi-entity client.
    //
    // Fire-and-forget and separately caught: this is a non-critical convenience save
    // and must never fail or delay login.
    vueInstance.$api.get("setup_guide/industry_info")
      .then((res) => {
        const info = res.data && res.data.data;
        if (info && info.industry_name) {
          localStorage.setItem("industry", info.industry_name);
        }
        if (info && Array.isArray(info.industry_ids) && info.industry_ids.length) {
          localStorage.setItem("industry_id", info.industry_ids[0]);
        }
        console.log("[login] industry info saved:", info);
      })
      .catch((e) => console.warn("[login] industry info lookup failed:", e?.message));

    let employee_dataset = response.data.emp_details;
    localStorage.setItem("Self_Rating_Setting", "yes");
    let content_binary = null;

    if (employee_dataset.length) {
      localStorage.setItem("LOGGED_EMP_ID", employee_dataset[0].id);
      localStorage.setItem("LOGGED_EMP_NO", employee_dataset[0].empno);

      for (let j = 0; j < employee_dataset.length; j++) {
        let d = employee_dataset[j].profile_pic || [];
        if (d.length) {
          if (d[0]?.content_binary) {
            content_binary = d[0]?.content_binary;
          }
        }
      }
    } else {
      localStorage.setItem("LOGGED_EMP_ID", 0);
      localStorage.setItem("LOGGED_EMP_NO", 0);
    }

    if (content_binary) {
      localStorage.setItem("LOGGED_EMP_PROFILE_IMG", content_binary);
    } else {
      localStorage.setItem("LOGGED_EMP_PROFILE_IMG", emptyProfilePic);
    }

    localStorage.setItem("APPRAISAL_PERIOD_MSTR", JSON.stringify(response.data.appraisal_period_mstr));

    // CUSTOMER_LIST from get_features_by_role response
    const customer = response.data.customer;
    if (Array.isArray(customer) && customer.length > 0) {
      localStorage.setItem("CUSTOMER_LIST", JSON.stringify(customer[0]));
    }

    // kpi_calc
    let calc_settings = response.data.calc_settings;
    if (calc_settings && calc_settings.length) {
      localStorage.setItem("kpi_calc", calc_settings[0].show === true ? "yes" : "no");
    } else {
      localStorage.setItem("kpi_calc", "yes");
    }

    // Self_Rating_Setting (override default with actual value)
    let rating_setting = response.data.rating_setting;
    if (rating_setting && rating_setting.length) {
      localStorage.setItem("Self_Rating_Setting", rating_setting[0].self_rating ? "yes" : "no");
    }

    // Set module and screen rights
    set_local_storage(response.data.modules_by_users, response.data.screen_by_users);

    // Process product list
    let pl = [];
    let product_list_item = response.data.products_by_users;
    if (product_list_item.length !== 0) {
      pl = product_list_item.map((a) => a.product_code);
    } else {
      pl = response.data.data[0].product_list;
    }

    let fl = response.data.data[0].roles_list;
    let features_list = fl == "" || fl == null ? [] : fl;
    let product_list = pl == "" || pl == null ? [] : pl;

    localStorage.setItem("tectalik_product_list", product_list);
    localStorage.setItem("features_list", features_list);
    localStorage.setItem("USER_ID", data.user_id);
    localStorage.setItem("USER_EMAIL", data.user_email);
    localStorage.setItem("USER_NAME", data.user_name);
    localStorage.setItem("BRANCH_ID", response.data.data[0].branch_id);
    localStorage.setItem("domain", domain);

    if (response.data.data) {
      localStorage.setItem("CHAIRMAN_FLAG", response.data.data[0].chairman_flag);
      localStorage.setItem("CEO_FLAG", response.data.data[0].ceo_flag);
      localStorage.setItem("SECRETARY_FLAG", response.data.data[0].secretary_flag);
      localStorage.setItem("USER_DESIGNATION", response.data.data[0].designation_name);
      localStorage.setItem("DEPT_ID", response.data.data[0].department_id || "");
    } else {
      localStorage.setItem("CHAIRMAN_FLAG", false);
      localStorage.setItem("CEO_FLAG", false);
      localStorage.setItem("SECRETARY_FLAG", false);
      localStorage.setItem("USER_DESIGNATION", '');
      localStorage.setItem("DEPT_ID", "");
    }

    // Determine redirect path
    let targetPath = "/my_work_inbox";


    let is_family_book = 'false'
    let is_boardpage = 'false' 

    if (product_type == 'STARR') {
      // Check screen auth for dashboard redirects
      if (vueInstance && typeof vueInstance.screen_auth === 'function') {
        if (data.first_login && vueInstance.screen_auth('SCN_COMMAND_CENTRE', 'landing')) {
          targetPath = "/command-centre";
        } else if (vueInstance.screen_auth('SCN_CEO_DASHBOARD', 'landing')) {
          targetPath = "/CEO_DashBoard";
        } else if (vueInstance.screen_auth('SCN_EMPLOYEE_DASHBOARD', 'landing')) {
          targetPath = "/employeedashboard";
        }
      }
    }else if (product_type == 'STARR_Board') {
      is_boardpage = 'true'
      targetPath = "/scorecard_new";
      if (response.data.data[0]?.chairman_flag === true || response.data.data[0]?.ceo_flag === true) {
        targetPath = "/BoardDashBoard";
      } else if (response.data.data[0]?.secretary_flag === true) {
        targetPath = "/SecretaryBoard";
      }
    }else if (product_type == 'STARR_FamilyBook') {
      is_family_book = 'true'
      targetPath = "/FamilyDashboard";
    } 

    localStorage.setItem("is_family_book", is_family_book);
    localStorage.setItem("is_boardpage", is_boardpage);
    localStorage.setItem("SUBSCRIPTION_STATUS",    response.data.subscription_status    || 'TRIAL');
    localStorage.setItem("TRIAL_ENDS_AT",          response.data.trial_ends_at           || '');
    localStorage.setItem("TRIAL_EXTENSION_COUNT",  response.data.trial_extension_count   ?? 0);
    localStorage.setItem("SUBSCRIPTION_END_DATE",  response.data.subscription_end_date   || '');
    // Drives which loop(s) the Solutions page shows (SolutionsHome.vue filters by these)
    localStorage.setItem("OFFERING_TYPE",          response.data.offering_type           || 'product');
    localStorage.setItem("SOLUTION_KEY",           response.data.solution_key            || '');

    // Keep resultat in sync so the version check in Home.vue doesn't false-alarm on reload
    localStorage.setItem("resultat", Package.version);

    // On first login for STARR, check license agreement then setup guide
    // Skip if already accepted (persisted in localStorage from a previous session)
    if (product_type === 'STARR' && !data.license_accepted && localStorage.getItem("LICENSE_ACCEPTED") !== "true") {
      router.push({ path: "/license-agreement" });
      return;
    }

    if (product_type === 'STARR' && data.first_login) {
      try {
        const entityId = localStorage.getItem("ENTITY_ID");
        const sgRes = await axios.get("setup_guide/progress", { params: { entity_id: entityId } });
        if (!sgRes.data.data || sgRes.data.data.length === 0) {
          router.push({ path: "/setup_guide" });
          return;
        }
      } catch (err) {
        console.warn("[loginHelper] Setup guide check failed, proceeding to default:", err);
      }
    }

    // Post-signup auto-login redirect (set by website registration flow via _alc token)
    const alcRedirect = sessionStorage.getItem('_alc_redirect');
    if (alcRedirect) {
      sessionStorage.removeItem('_alc_redirect');
      router.push({ path: alcRedirect });
      return;
    }

    if ((router.currentRoute.value?.path ?? router.currentRoute.path) === targetPath) {
      window.location.reload();
    } else {
      router.push({ path: targetPath });
    }

  } catch (error) {
    console.error("Error in processCompanyLogin:", error);
    throw error;
  }
}

/**
 * Process admin login
 */
async function processAdminLogin(data, domain, router, vueInstance) {
  try {
    const response = await axios.get("api/get_screen_and_user_data_by_role/", {
      params: { user_id: data.user_id },
    });

    const userTypeCode = response.data.data.user_type_code;
    localStorage.setItem("USER_TYPE", userTypeCode);
    localStorage.setItem("USER_TYPES", userTypeCode);

    let screen_dataset = [];
    let module_dataset = [];
    localStorage.setItem("SCREEN_RIGHT_DATASET", JSON.stringify(screen_dataset));
    localStorage.setItem("MODULE_RIGHTS_DATASET", JSON.stringify(module_dataset));

    let common_screen_dataset = response.data.common_screen_dataset;
    localStorage.setItem("COMMON_SCREEN_RIGHT_DATASET", JSON.stringify(common_screen_dataset));
    localStorage.setItem("tectalik_product_list", []);
    localStorage.setItem("features_list", []);
    localStorage.setItem("TOKEN", data.token);
    localStorage.setItem("USER_ID", data.user_id);
    localStorage.setItem("USER_EMAIL", data.user_email);
    localStorage.setItem("USER_NAME", data.user_name);
    localStorage.setItem("domain", domain);

    // NOTE (20260309121500): Partner login — if email matches a partner record, redirect to partner dashboard
    if (response.data.partner_id) {
      localStorage.setItem("PARTNER_ID", response.data.partner_id);
      router.push({ path: "/partner-dashboard" });
    } else if (userTypeCode === "HEALTHCHECK") {
      // Healthcheck users have their own portal — no admin menu shown
      localStorage.removeItem("PARTNER_ID");
      router.push({ path: "/healthcheck_portal" });
    } else if (userTypeCode === "workshop_user" || userTypeCode === "workshop_expert") {
      // Workshop participants and experts go straight to the workshop portal
      localStorage.removeItem("PARTNER_ID");
      router.push({ path: "/workshop" });
    } else {
      localStorage.removeItem("PARTNER_ID");
      router.push({ path: "/admin_landing" });
    }

  } catch (error) {
    console.error("Error in processAdminLogin:", error);
    throw error;
  }
}

/**
 * Get domain logo
 */
async function getDomainLogo(domain, vueInstance) {
  try {
    if (vueInstance && vueInstance.$papi) {
      const res = await vueInstance.$papi.get("get_domain_logo_by_domain", {
        params: { domain: domain },
      });

      let logoUrl = null;
      if (res.data.data && res.data.data.logo) {
        if (res.data.data.logo.startsWith('https://')) {
          logoUrl = res.data.data.logo;
        } else {
          logoUrl = `https://devcdn.tectalik.com/compliance/uploads/original_${res.data.data.logo}`;
          localStorage.setItem("logoUrl", logoUrl);
        }
      }
    }
  } catch (err) {
    console.error("Error fetching logo:", err);
  }
}

/**
 * Set module and screen rights in localStorage
 */
function set_local_storage(module_dataset = [], screen_dataset = []) {
  localStorage.setItem("MODULE_RIGHTS_DATASET", JSON.stringify(module_dataset));

  for (let i = 0; i < screen_dataset.length; i++) {
    screen_dataset[i]["hide_all"] = false;
    screen_dataset[i]["module_name"] = false;
    for (let j = 0; j < module_dataset.length; j++) {
      if (screen_dataset[i].modules_rights_id === module_dataset[j].modules_rights_id) {
        screen_dataset[i]["hide_all"] = module_dataset[j].active;
        screen_dataset[i]["module_name"] = module_dataset[j].module_name;
      }
    }
  }

  localStorage.setItem("SCREEN_RIGHT_DATASET", JSON.stringify(screen_dataset));
}

/**
 * Full login handler - processes login response and sets up localStorage
 * @param {Object} data - Login response data (response.data)
 * @param {string} domain - domain prefix
 * @param {Object} router - Vue router instance
 * @param {Object} vueInstance - Vue component instance
 * @param {string} applicationVersion - app version
 */
export async function handleLogin(data, domain, router, vueInstance, applicationVersion = null, product_type = 'STARR') {
  await processLoginResponse(data, domain, router, vueInstance, applicationVersion, product_type);
}

/**
 * SSO Login handler - for use with SSO verification
 * @param {Object} data - Login response data (already extracted from response)
 * @param {string} domain - domain prefix
 * @param {Object} router - Vue router instance
 * @param {Object} vueInstance - Vue component instance
 * @param {string} applicationVersion - app version
 */
export async function handleSsoLogin(data, domain, router, vueInstance, applicationVersion = null, product_type = 'STARR') {
  await processLoginResponse(data, domain, router, vueInstance, applicationVersion, product_type);
}

// NOTE (20260304150000): starrboard.com login_by_domain entity switch.
// After user picks a company/entity from SwitchCompanyDialog, this function:
// 1. Calls POST /api/sessions/switch_entity to get a schema-specific token
// 2. Sets TOKEN in localStorage
// 3. Reuses processCompanyLogin for full localStorage setup + navigation
// Used by LoginExisting.vue (on login) and TopNavBar.vue (switch company).
/**
 * @param {string} email - logged-in user's email
 * @param {Object} entity - selected entity from login_by_domain entities[]
 *   { userid, entity_id, entity_name, entity_name_actual, sub_domain, finyear, ... }
 * @param {Object} router - Vue router instance
 * @param {Object} vueInstance - Vue component instance ($papi and $api must be available)
 * @param {string} product_type - 'STARR' | 'STARR_Board' | 'STARR_FamilyBook'
 */
export async function processDomainEntitySwitch(email, entity, router, vueInstance, product_type = 'STARR') {
  const res = await vueInstance.$papi.post("sessions/switch_entity", {
    email: email,
    entity_id: entity.entity_id
  });

  if (res.data.result !== "success") {
    throw new Error("[ERR_AUTH_003] Entity switch failed: " + (res.data.message || "Unknown error"));
  }

  // Set schema-specific token before get_features_by_role call
  localStorage.setItem("TOKEN", res.data.token);

  // Clear any payment block carried over from the PREVIOUS entity — without
  // this, main.js's router.beforeEach guard sees the stale flag and bounces
  // the user straight back to /payment-required even though the newly
  // switched-to entity may be in perfectly good standing.
  localStorage.removeItem("PAYMENT_BLOCKED");

  // Entity already chosen — store it so OldHomeFunctions.checkAndOpenDialog
  // sees storedEntityId and does NOT show the "Switch Options" dialog again.
  localStorage.setItem("ENTITY_ID", entity.entity_id);
  if (entity.entity_name) localStorage.setItem("entity_name", entity.entity_name);

  // Store entity_url_id — used for entity-prefixed URL routing (ENTITY_URL_PLAN.md)
  if (res.data.entity_url_id) localStorage.setItem("ENTITY_URL_ID", res.data.entity_url_id);

  // Derive host from product_type and store so the host dialog is also skipped.
  const derivedHost = product_type === 'STARR_Board' ? 'board'
                    : product_type === 'STARR_FamilyBook' ? 'familybook'
                    : 'starr';
  localStorage.setItem("SELECTED_HOST", derivedHost);
  localStorage.setItem("selected_host_flag", derivedHost);

  // Entity already chosen — skip the "Switch Options" host dialog on show_list
  sessionStorage.setItem("host_dialog_shown", "true");

  // CUSTOMER_LIST, kpi_calc, Self_Rating_Setting from switch_entity response
  const customer = res.data.customer;
  if (Array.isArray(customer) && customer.length > 0) {
    localStorage.setItem("CUSTOMER_LIST", JSON.stringify(customer[0]));
  }
  const calc_settings = res.data.calc_settings;
  if (calc_settings && calc_settings.length) {
    localStorage.setItem("kpi_calc", calc_settings[0].show === true ? "yes" : "no");
  } else {
    localStorage.setItem("kpi_calc", "yes");
  }
  const rating_setting = res.data.rating_setting;
  if (rating_setting && rating_setting.length) {
    localStorage.setItem("Self_Rating_Setting", rating_setting[0].self_rating ? "yes" : "no");
  } else {
    localStorage.setItem("Self_Rating_Setting", "yes");
  }

  localStorage.setItem("CLIENT_COUNTRY", res.data.country || "");
  localStorage.setItem("CLIENT_CURRENCY", res.data.currency || "");

  const loginData = {
    result: "success",
    token: res.data.token,
    user_id: res.data.userid,
    user_email: res.data.user_email,
    user_name: res.data.user_name,
    first_login: res.data.first_login === true,
    license_accepted: res.data.license_accepted === true
  };

  await processCompanyLogin(loginData, entity.sub_domain, router, vueInstance, product_type);
}
